<script setup lang="ts">
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { onMounted, ref, watch } from 'vue';
import { useFirestore, useIsCurrentUserLoaded } from 'vuefire';
import { useDebugUser } from '@/composables/useDebugUser';
import HandicapModal from './HandicapModal.vue';

const showHandicapModal = ref(false);
const user = useDebugUser();
const isUserLoaded = useIsCurrentUserLoaded();
const db = useFirestore();
const checkingHandicap = ref(false);
const hasCheckedHandicap = ref(false);
const unsubscribe = ref<(() => void) | null>(null);

// Watch for user login and check for handicap when user changes
// and the auth state is fully loaded
watch([user, isUserLoaded], async ([newUser, loaded]) => {
  if (newUser && loaded && !hasCheckedHandicap.value) {
    setupUserMonitoring(newUser.uid);
  }
  else if (!newUser && loaded) {
    // Clean up listener if user logs out
    if (unsubscribe.value) {
      unsubscribe.value();
      unsubscribe.value = null;
    }
    hasCheckedHandicap.value = false;
  }
}, { immediate: true });

// Use Firestore onSnapshot to listen for changes to the user document
// This ensures we catch the document creation even if it happens after
// the initial check
function setupUserMonitoring(userId: string) {
  checkingHandicap.value = true;

  // First check immediately if document already exists
  checkUserHandicap(userId);

  // Then set up a listener for changes to the document
  const userDocRef = doc(db, 'users', userId);
  unsubscribe.value = onSnapshot(userDocRef, (docSnapshot) => {
    if (docSnapshot.exists() && !hasCheckedHandicap.value) {
      const userData = docSnapshot.data();
      if (userData.handicap === undefined || userData.handicap === null) {
        showHandicapModal.value = true;
        hasCheckedHandicap.value = true;
      }
      checkingHandicap.value = false;
    }
  }, (error) => {
    console.error('Error listening to user document:', error);
    checkingHandicap.value = false;
  });
}

async function checkUserHandicap(userId: string) {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (userDoc.exists()) {
      const userData = userDoc.data();

      // If the user document exists but handicap is not set or null, show the modal
      if ((userData.handicap === undefined || userData.handicap === null) && !hasCheckedHandicap.value) {
        showHandicapModal.value = true;
        hasCheckedHandicap.value = true;
      }
    }
    // If document doesn't exist, the onSnapshot listener will catch it when created
  }
  catch (error) {
    console.error('Error checking handicap:', error);
  }
  finally {
    // Only set checking to false if we're not still waiting for the document to be created
    if (unsubscribe.value === null) {
      checkingHandicap.value = false;
    }
  }
}

function closeModal() {
  showHandicapModal.value = false;
}

// Clean up listener when component is unmounted
onMounted(() => {
  return () => {
    if (unsubscribe.value) {
      unsubscribe.value();
      unsubscribe.value = null;
    }
  };
});
</script>

<template>
  <HandicapModal
    v-if="user && !checkingHandicap"
    :visible="showHandicapModal"
    :user-id="user.uid"
    @close="closeModal"
  />
</template>
